import { useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Timeline from "@mui/icons-material/Timeline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { CupaPreferenceList } from "components/CupaPreferences/CupaPreferenceList";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { ResolutionStatus } from "generated-graphql/graphql";
import { GET_CUPA_PREFERENCE_PROFILE } from "hooks/useCupaPreferenceProfile";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { CreateRevisionAction } from "../CreateRevisionAction";
import { decisionStatusColor, facilityTimelineUrl, formatDate } from "../Submittals/cersStatus";
import { AcknowledgeDialog } from "./AcknowledgeDialog";
import { DismissDialog } from "./DismissDialog";
import { findingAgeDays, resolutionStatusColor } from "./rejectionStatus";

// status/resolutionType/resolvedAt (and history's status/resolutionType) aren't
// all rendered, but they keep this selection aligned with the dismiss/acknowledge
// mutation responses so the cache entry updates coherently after an action.
const FINDING_DETAIL_QUERY = gql(`
  query CersFindingDetail($id: ID!) {
    cersFinding(id: $id) {
      id
      cersFacilityId
      rejectedSubmittalId
      module
      status
      resolutionType
      cupaName
      cupaCode
      facilityName
      encampFacilityId
      encampFacilityName
      encampTenantId
      tenantName
      detectedAt
      resolvedAt
      revisionReportId
      rejectedDecision {
        id
        status
        decisionDate
        rawComment
        regulatorName
        regulatorEmail
        regulatorPhone
        commentType
        issueCategories
        llmSummary
        extractedContacts {
          name
          email
          phone
          role
        }
      }
      history {
        id
        eventType
        status
        resolutionType
        note
        actorName
        createdAt
      }
      facilitySubmittals {
        id
        submittedDate
        submittedBy
        moduleDecisions {
          id
          module
          status
        }
      }
    }
  }
`);

type Props = {
  /** Finding to show, or null when the drawer is closed. Driven by ?findingId=. */
  findingId: string | null;
  onClose: () => void;
};

export function RejectionActionDrawer({ findingId, onClose }: Props) {
  const open = findingId !== null;

  const [dismissOpen, setDismissOpen] = useState(false);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);

  const { data, loading, error } = useQuery(FINDING_DETAIL_QUERY, {
    variables: { id: findingId ?? "" },
    skip: !findingId,
    fetchPolicy: "cache-and-network",
  });

  const finding = data?.cersFinding ?? null;
  const notFound = Boolean(findingId && data && !finding && !error);
  const decision = finding?.rejectedDecision ?? null;

  // Title links to the matched Encamp facility in a NEW tab so staff can fix
  // whatever the CUPA flagged while keeping the finding open side-by-side —
  // CUPA comments are often a checklist, worked back-and-forth.
  const displayName = finding?.encampFacilityName ?? finding?.facilityName ?? "";
  const facilityUrl =
    finding?.encampFacilityId && finding.encampTenantId
      ? `/o/${finding.encampTenantId}/facilities/${finding.encampFacilityId}`
      : null;
  const title = facilityUrl ? (
    <MuiLink
      component={RouterLink}
      to={facilityUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      {displayName}
      <OpenInNew fontSize="small" />
    </MuiLink>
  ) : (
    displayName
  );

  const isOpenStatus =
    finding?.status === ResolutionStatus.Unresolved || finding?.status === ResolutionStatus.Pending;

  const footer = finding ? (
    <Stack spacing={1.5}>
      {finding.revisionReportId ? (
        <Alert severity="info">
          A revision has been filed for this finding.{" "}
          <MuiLink component={RouterLink} to={`/staff/fulfillment/r/${finding.revisionReportId}`}>
            <OpenInNew fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.25 }} />
            View revision
          </MuiLink>
        </Alert>
      ) : (
        isOpenStatus && (
          <CreateRevisionAction
            findingId={finding.id}
            isFacilityMatched={Boolean(finding.encampFacilityId)}
            cersFacilityId={finding.cersFacilityId}
          />
        )
      )}
      {isOpenStatus && (
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" color="warning" onClick={() => setDismissOpen(true)}>
            Dismiss
          </Button>
          <Button fullWidth variant="outlined" onClick={() => setAcknowledgeOpen(true)}>
            Acknowledge
          </Button>
        </Stack>
      )}
    </Stack>
  ) : null;

  return (
    <>
      {/* nonModal: the grid stays clickable behind the drawer, so triage flows
          row → drawer → next row without closing (URL param retargets it). */}
      <RightDrawerShell open={open} onClose={onClose} title={title} footer={footer} nonModal>
        {error && (
          <Alert severity="error">
            Failed to load this finding. Close the drawer and try again.
          </Alert>
        )}
        {notFound && !loading && (
          <Alert severity="error">
            This CERS rejection finding could not be found. It may have been removed, or the link
            may be stale.
          </Alert>
        )}
        {loading && !finding && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {finding && (
          <Stack spacing={2.5}>
            {/* ── Summary ── */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <StatusChip
                  label={prettyPrintEnumValue(finding.status)}
                  color={resolutionStatusColor(finding.status)}
                />
                <Typography variant="body2" color="text.secondary">
                  {prettyPrintEnumValue(finding.module)}
                  {finding.cupaName ? ` · ${finding.cupaName}` : ""}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                sx={{ color: "text.secondary", typography: "body2", mt: 0.5 }}
              >
                {finding.tenantName && <span>{finding.tenantName}</span>}
                <span>Decision {formatDate(decision?.decisionDate)}</span>
                {(() => {
                  const age = findingAgeDays({
                    decisionDate: decision?.decisionDate,
                    detectedAt: finding.detectedAt,
                  });
                  return age !== null ? <span>{age}d open</span> : null;
                })()}
              </Stack>
              {decision?.llmSummary && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {decision.llmSummary}
                </Typography>
              )}
            </Box>

            {/* ── CUPA comment (verbatim — never reworded or trimmed) ── */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                CUPA comment
              </Typography>
              {decision?.commentType ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, gap: 0.5 }}>
                  <StatusChip label={prettyPrintEnumValue(decision.commentType)} color="warning" />
                  {decision.issueCategories.map((category) => (
                    <StatusChip
                      key={category}
                      label={prettyPrintEnumValue(category)}
                      color="default"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Not yet classified
                </Typography>
              )}
              {decision?.rawComment ? (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {decision.rawComment}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No comment
                </Typography>
              )}
            </Box>

            {/* ── Contacts ── */}
            {(decision?.regulatorName ||
              decision?.regulatorEmail ||
              decision?.regulatorPhone ||
              (decision?.extractedContacts.length ?? 0) > 0) && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  CUPA contacts
                </Typography>
                {(decision?.regulatorName ||
                  decision?.regulatorEmail ||
                  decision?.regulatorPhone) && (
                  <Stack spacing={0.25}>
                    {decision.regulatorName && (
                      <Typography variant="caption" color="text.secondary">
                        {decision.regulatorName}
                      </Typography>
                    )}
                    {decision.regulatorEmail && (
                      <Typography variant="caption" color="text.secondary">
                        {decision.regulatorEmail}
                      </Typography>
                    )}
                    {decision.regulatorPhone && (
                      <Typography variant="caption" color="text.secondary">
                        {decision.regulatorPhone}
                      </Typography>
                    )}
                  </Stack>
                )}
                {(decision?.extractedContacts.length ?? 0) > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ letterSpacing: 0.6, lineHeight: 1 }}
                    >
                      Extracted from comment
                    </Typography>
                    <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                      {decision?.extractedContacts.map((contact) => (
                        <Typography
                          key={[contact.name, contact.email, contact.phone, contact.role]
                            .map((v) => v ?? "")
                            .join("|")}
                          variant="caption"
                          color="text.secondary"
                        >
                          {[contact.name, contact.role, contact.email, contact.phone]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
            {/* ── Facility timeline ── */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Facility timeline
              </Typography>
              {finding.facilitySubmittals.length > 0 ? (
                <Stack spacing={1}>
                  {finding.facilitySubmittals.map((submittal) => {
                    const isRejectedSubmittal = submittal.id === finding.rejectedSubmittalId;
                    return (
                      <Box
                        key={submittal.id}
                        data-testid={`timeline-submittal-${submittal.id}`}
                        data-rejected={isRejectedSubmittal ? "true" : "false"}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: 1,
                          borderColor: isRejectedSubmittal ? "warning.main" : "divider",
                          backgroundColor: isRejectedSubmittal ? "action.selected" : "transparent",
                        }}
                      >
                        <Typography variant="body2">
                          Submitted {formatDate(submittal.submittedDate)}
                          {submittal.submittedBy ? ` by ${submittal.submittedBy}` : ""}
                          {isRejectedSubmittal ? " — this rejection" : ""}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          sx={{ mt: 0.5, gap: 0.5 }}
                        >
                          {submittal.moduleDecisions.map((d) => (
                            <StatusChip
                              key={d.id}
                              label={`${prettyPrintEnumValue(d.module)}: ${prettyPrintEnumValue(d.status)}`}
                              color={decisionStatusColor(d.status)}
                            />
                          ))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No other submittals recorded for this facility.
                </Typography>
              )}
              {finding.facilityName && (
                <Button
                  component={RouterLink}
                  to={facilityTimelineUrl(finding.facilityName)}
                  startIcon={<Timeline />}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                >
                  View full timeline
                </Button>
              )}
            </Box>

            {/* ── Resolution history ── */}
            {finding.history.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Resolution history
                </Typography>
                <Stack spacing={0.5}>
                  {finding.history.map((ev) => (
                    <Typography key={ev.id} variant="caption" color="text.secondary">
                      {new Date(ev.createdAt).toLocaleString("en-US")} —{" "}
                      {prettyPrintEnumValue(ev.eventType)}
                      {" by "}
                      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {ev.actorName}
                      </Box>
                      {ev.note ? `: ${ev.note}` : ""}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            <CupaPreferenceSnippet cupaCode={finding.cupaCode} />
          </Stack>
        )}
      </RightDrawerShell>

      {finding && (
        <>
          <DismissDialog
            open={dismissOpen}
            findingId={finding.id}
            onClose={() => setDismissOpen(false)}
            onDone={() => setDismissOpen(false)}
          />
          <AcknowledgeDialog
            open={acknowledgeOpen}
            findingId={finding.id}
            onClose={() => setAcknowledgeOpen(false)}
            onDone={() => setAcknowledgeOpen(false)}
          />
        </>
      )}
    </>
  );
}

function CupaPreferenceSnippet({ cupaCode }: { cupaCode?: string | null }) {
  const { data, loading } = useQuery(GET_CUPA_PREFERENCE_PROFILE, {
    variables: { cupaCode: cupaCode ?? "" },
    skip: !cupaCode,
  });
  if (!cupaCode) return null;
  const profile = data?.cupaPreferenceProfile;
  // Suppress the empty-state flash while the profile query is in-flight.
  if (loading && !profile) return null;
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        CUPA preferences
      </Typography>
      {profile ? (
        <>
          <CupaPreferenceList preferences={profile.preferences} max={3} />
          <MuiLink
            component={RouterLink}
            to={`/staff/cers/cupas/${cupaCode}`}
            sx={{ display: "inline-block", mt: 1 }}
          >
            View full playbook →
          </MuiLink>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No preference profile yet for this CUPA.
        </Typography>
      )}
    </Box>
  );
}
